import { createPortal } from 'react-dom';
import { useLayoutEffect, useState } from 'react';
import { css } from '@emotion/css';

interface Rect {
  top: number;
  left: number;
  width: number;
}

interface PopoverPanelProps {
  visible: boolean;
  closing: boolean;
  rect: Rect | null;
  panelRef: React.RefObject<HTMLDivElement>;
  transitionMs: number;
  children: React.ReactNode;
  offsetHeight: number;
}

export const PopoverPanel = ({
  visible,
  closing,
  rect,
  panelRef,
  transitionMs,
  children,
  offsetHeight,
}: PopoverPanelProps) => {
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
  const [computedTop, setComputedTop] = useState(rect?.top ?? 0);

  // 根据视口空间动态调整浮层是向下展开还是向上展开
  useLayoutEffect(() => {
    if (!rect || !visible) return;
    const panelEl = panelRef.current;
    if (!panelEl) return;

    const panelRect = panelEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 4;
    // 向上展开时额外预留一段偏移量，避免遮挡触发区域
    const upwardOffset = offsetHeight + 12;

    const willOverflowBottom = rect.top + margin + panelRect.height > viewportHeight;
    const canPlaceTop = rect.top - margin - upwardOffset - panelRect.height > 0;

    if (willOverflowBottom && canPlaceTop) {
      setPlacement('top');
      setComputedTop(rect.top - panelRect.height - margin - upwardOffset);
    } else {
      setPlacement('bottom');
      setComputedTop(rect.top + margin);
    }
  }, [rect, visible, panelRef, offsetHeight]);

  if ((!visible && !closing) || rect === null) {
    return null;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={panelStaticCls(transitionMs)}
      style={{
        top: computedTop,
        left: rect.left,
        width: rect.width,
        opacity: visible ? 1 : 0,
        transform: visible
          ? 'translateY(0)'
          : placement === 'bottom'
          ? 'translateY(-8px)'
          : 'translateY(8px)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const panelStaticCls = (transitionMs: number) => css`
  position: fixed;
  z-index: 1050;
  background: var(--fett-bg-elevated);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 8px 12px;
  transition: opacity ${transitionMs}ms ease, transform ${transitionMs}ms ease;
`;
